import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSubscription } from './useSubscription';

interface OnboardingStep {
  id: string;
  label: string;
  completed: boolean;
  path?: string;
}

interface OnboardingProgress {
  steps: OnboardingStep[];
  completionPercentage: number;
  isFullyComplete: boolean;
  isLoading: boolean;
}

const STEP_IDS = {
  INDUSTRY: 'industry',
  INSTAGRAM: 'instagram',
  INSPIRATION: 'inspiration',
  SUBSCRIPTION: 'subscription',
} as const;

export const useOnboardingProgress = (): OnboardingProgress => {
  const { user } = useAuth();
  const { subscribed, loading: subLoading } = useSubscription();
  const [progress, setProgress] = useState<OnboardingProgress>({
    steps: [],
    completionPercentage: 0,
    isFullyComplete: false,
    isLoading: true,
  });

  useEffect(() => {
    const checkProgress = async () => {
      if (!user?.id || subLoading) {
        if (!subLoading) {
          setProgress({
            steps: [],
            completionPercentage: 0,
            isFullyComplete: false,
            isLoading: false,
          });
        }
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('category, instagram_username')
          .eq('user_id', user.id)
          .maybeSingle();

        const { count: sourcesCount } = await supabase
          .from('inspiration_sources')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        const steps: OnboardingStep[] = [
          {
            id: STEP_IDS.INSTAGRAM,
            label: 'Dodaj nazwę konta Instagram',
            completed: !!profile?.instagram_username,
            path: '/ustawienia',
          },
          {
            id: STEP_IDS.INDUSTRY,
            label: 'Wybierz swoją branżę',
            completed: !!profile?.category,
            path: '/ustawienia',
          },
          {
            id: STEP_IDS.INSPIRATION,
            label: 'Dodaj źródła inspiracji',
            completed: (sourcesCount ?? 0) > 0,
            path: '/inspiracje',
          },
          {
            id: STEP_IDS.SUBSCRIPTION,
            label: 'Aktywuj subskrypcję',
            completed: subscribed,
            path: '/ustawienia',
          },
        ];

        const completedCount = steps.filter(step => step.completed).length;
        const completionPercentage = Math.round((completedCount / steps.length) * 100);
        const isFullyComplete = completedCount === steps.length;

        setProgress({
          steps,
          completionPercentage,
          isFullyComplete,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error checking onboarding progress:', error);
        setProgress({
          steps: [],
          completionPercentage: 0,
          isFullyComplete: false,
          isLoading: false,
        });
      }
    };

    checkProgress();
  }, [user?.id, subLoading, subscribed]);

  return progress;
};
